import { useMemo } from 'react';
import { useDrillsData } from './domain/useDrillsData';
import { useDrillsConfig } from './domain/drills/useDrillsConfig';
import { useCrew } from './useCrew';
import { useDefects } from './domain/defects/useDefects';
import { useMaintenanceSchedule } from './domain/useMaintenanceSchedule';
import { useMaintenanceHistory } from './domain/maintenance/useMaintenanceHistory';
import { useVessel } from '../contexts/VesselContext';
import { Flame, Wrench, Users, AlertCircle } from 'lucide-react';
import { useStorageState } from './useStorageState';
import { useHoursOfRestData } from './domain/useHoursOfRestData';
import { getCrewCompliance } from '../components/tailwind/modules/crew/utils/crewHelpers';
// FT-140 FIX: Removed getJobFrequencyDays - it uses +30 days which violates maritime standards
// Dashboard now uses calculateNextDue() which matches MaintenanceJobCardEnhanced.js (calendar months)
import { summarizeJob } from '../services/equipmentMaintenanceService';
import { getJobDisplayName } from '../utils/maintenanceJobHelpers';

/**
 * Hook to get dashboard category data for the category cards
 * Returns array of categories with their top 3 upcoming items
 */
export const useDashboardCategories = () => {
  const drillsData = useDrillsData();
  // FT-471: Use user-curated drill list (WBC3 mandatory + company required) instead of
  // auto-populated drill_register which contained ghost entries (GMDSS DSC etc.)
  const { drillList } = useDrillsConfig();
  const { crew } = useCrew();
  const defectsData = useDefects();
  // FT-92 FIX: Get loaded flag to debug timing issues
  const { schedule: maintenanceSchedule, loaded: maintenanceLoaded } = useMaintenanceSchedule();
  const { history: maintenanceHistory } = useMaintenanceHistory();

  // FT-92 DEBUG: Log maintenance schedule loading state
  if (process.env.NODE_ENV !== 'production') {
    console.log('[useDashboardCategories] Maintenance:', {
      loaded: maintenanceLoaded,
      jobCount: maintenanceSchedule?.length || 0,
      jobs: maintenanceSchedule?.slice?.(0, 3)?.map(j => j.title || j.item) || []
    });
  }
  const { currentVesselId, getCurrentVessel } = useVessel();
  const currentVessel = getCurrentVessel();

  // FT-92B: Get advanceWarningDays from maintenance config (same as PlannedMaintenanceTab)
  const { value: maintenanceConfig = {} } = useStorageState('maintenance_config', {});
  const advanceWarningDays = maintenanceConfig?.advanceWarningDays || 30;

  // FT-164: Use new flat array hook
  const { data: horRecords = [] } = useHoursOfRestData();

  // FT-164: Convert flat array to legacy nested lookup for compatibility
  const hoursData = useMemo(() => {
    const nested = {};
    for (const record of horRecords) {
      if (!record.crewId || !record.date) continue;
      if (!nested[record.crewId]) nested[record.crewId] = {};
      nested[record.crewId][record.date] = record;
    }
    return nested;
  }, [horRecords]);
  const { value: familiarisationRecords = [] } = useStorageState('familiarisation_records', []);
  const { value: familiarisationSettings = {} } = useStorageState('familiarisationSettings', {});
  const familiarisationValidityMonths = familiarisationSettings.validityPeriodMonths || 12;
  const drillsKey = currentVesselId ? `drills_${currentVesselId}` : 'drills';
  const { value: drillsStorageData = [] } = useStorageState(drillsKey, []);

  // FT-140: Load equipment register for summarizeJob (single source of truth for maintenance dates)
  const { value: equipmentRegister = [] } = useStorageState('equipment_register', []);

  const categories = useMemo(() => {
    const result = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // ========================================
    // DRILLS CATEGORY
    // FT-471: Reads from drillList (useDrillsConfig) — the user-curated list
    // (WBC3 mandatory: Fire, Abandon Ship, MOB + company-required drills).
    // Previously read from drill_register_<vesselId> which was auto-populated
    // with ghost entries (GMDSS DSC, duplicate Fire/Abandon Ship etc.).
    // Next-due dates are derived from actual completion records in drills_<vesselId>.
    // ========================================
    try {
      // FT-458: tombstone-filtered view so a soft-deleted drill can't count.
      const allDrills = drillsData?.visibleDrills || drillsData?.drills || [];

      const drillItems = (drillList || [])
        .map(drill => {
          const drillType = drill.type || drill.drillType;
          let nextDueDate = null;

          // Derive next due from last completion + frequency
          const completedDrills = allDrills
            .filter(d => d.type === drillType && d.date)
            .sort((a, b) => new Date(b.date) - new Date(a.date));

          if (completedDrills.length > 0) {
            const lastCompletedDate = new Date(completedDrills[0].date);
            const frequencyMs = (drill.frequencyDays || 30) * 24 * 60 * 60 * 1000;
            nextDueDate = new Date(lastCompletedDate.getTime() + frequencyMs);
          } else {
            // Never completed — show as overdue (due today)
            nextDueDate = today;
          }

          const isOverdue = nextDueDate < today;
          const daysUntilDue = Math.ceil((nextDueDate - today) / (1000 * 60 * 60 * 24));

          return {
            id: `drill-${drillType}`,
            icon: Flame,
            title: drill.name || drillType,
            subtitle: `due ${formatDate(nextDueDate)}`,
            drillType, // Used for drill image selection
            dueDate: nextDueDate,
            isOverdue,
            isDueSoon: !isOverdue && daysUntilDue <= 7,
            status: isOverdue ? 'overdue' : daysUntilDue <= 7 ? 'due-soon' : 'ok',
            module: 'drills'
          };
        })
        .sort((a, b) => {
          // Overdue first, then by due date (soonest first)
          if (a.isOverdue && !b.isOverdue) return -1;
          if (!a.isOverdue && b.isOverdue) return 1;
          return a.dueDate - b.dueDate;
        })
        .slice(0, 10); // Show up to 10 drills (scrollable)

      if (drillItems.length > 0) {
        result.push({
          id: 'drills',
          title: 'Drills',
          icon: Flame,
          iconColor: 'text-orange-600',
          bgColor: 'bg-orange-50',
          module: 'drills',
          items: drillItems
        });
      }
    } catch (error) {
      console.error('[useDashboardCategories] Drills error:', error);
    }

    // ========================================
    // MAINTENANCE CATEGORY
    // FT-140: Uses summarizeJob() for SINGLE SOURCE OF TRUTH on maintenance dates
    // This is the same function PlannedMaintenanceTab uses, ensuring Dashboard
    // and Maintenance module show identical dates.
    // ========================================
    try {
      // FT-140: Create equipment map for summarizeJob (same as PlannedMaintenanceTab)
      const equipmentMap = new Map((equipmentRegister || []).map(eq => [eq.id, eq]));

      // FT-140 DEBUG: Log input data for maintenance card
      if (process.env.NODE_ENV !== 'production') {
        console.log('[useDashboardCategories] MAINTENANCE INPUT (FT-140):', {
          scheduleLength: maintenanceSchedule?.length || 0,
          historyLength: maintenanceHistory?.length || 0,
          equipmentCount: equipmentMap.size,
          firstJob: maintenanceSchedule?.[0] ? {
            id: maintenanceSchedule[0].id,
            title: maintenanceSchedule[0].title || maintenanceSchedule[0].item
          } : null
        });
      }

      const maintenanceItems = (maintenanceSchedule || [])
        .map(job => {
          // FT-140: Pass maintenanceHistory to summarizeJob as SOURCE OF TRUTH
          // summarizeJob now uses history directly - no more separate fallback needed
          const summary = summarizeJob(job, equipmentMap, maintenanceHistory);

          // FT-140: lastCompleted now comes directly from summarizeJob (via maintenanceHistory)
          const lastCompleted = summary.lastCompleted;

          // FT-140: Calculate nextDue exactly as PlannedMaintenanceTab does (lines 360-384)
          let nextDue = null;
          let daysUntilDue = null;

          if (summary.nextDue) {
            // Has explicit nextDue from summarizeJob
            // FT-140: Use parseLocalDate to avoid timezone shift
            nextDue = parseLocalDate(summary.nextDue);
            nextDue.setHours(0, 0, 0, 0);
            daysUntilDue = Math.ceil((nextDue - today) / (1000 * 60 * 60 * 24));
          } else if (lastCompleted) {
            // FT-140 FIX: Use calendar month math (maritime standard per MCA/ISM Code)
            // - Monthly jobs: setMonth() not +30 days (March 25 + 1 month = April 25)
            // - Annual jobs: setFullYear() not +365 days
            // - Weekly jobs: +7 days is fine
            // This matches MaintenanceJobCardEnhanced.js exactly (source of truth)
            const lastDate = parseLocalDate(lastCompleted);
            nextDue = calculateNextDue(lastDate, job.interval, job.unit);
            if (nextDue) {
              nextDue.setHours(0, 0, 0, 0);
              daysUntilDue = Math.ceil((nextDue - today) / (1000 * 60 * 60 * 24));
            } else {
              // Fallback if calculateNextDue returns null (missing interval/unit)
              nextDue = today;
              daysUntilDue = 0;
            }
          } else {
            // Never completed - due today
            nextDue = today;
            daysUntilDue = 0;
          }

          // FT-140: Status logic exactly as PlannedMaintenanceTab (lines 386-396)
          let status;
          let isOverdue = false;
          let isDueSoon = false;

          if (!lastCompleted) {
            status = 'due_soon';    // Never done → yellow
            isDueSoon = true;
          } else if (daysUntilDue < 0) {
            status = 'overdue';     // Past due → red
            isOverdue = true;
          } else if (daysUntilDue <= advanceWarningDays) {
            status = 'due_soon';    // Within threshold → yellow
            isDueSoon = true;
          } else {
            status = 'compliant';   // All good → green
          }

          const hasDate = nextDue && !isNaN(nextDue.getTime());
          const jobName = getJobDisplayName(job);

          // FT-140 DEBUG: Log detailed date calculation for comparison with Maintenance page
          console.log('[FT-140 DASHBOARD]', {
            jobName,
            jobId: job.id,
            summaryNextDue: summary.nextDue,
            summaryLastCompleted: summary.lastCompleted,
            lastCompletedUsed: lastCompleted,
            calculatedNextDue: nextDue?.toISOString?.()?.split('T')[0],
            daysUntilDue,
            status,
            isOverdue,
            isDueSoon,
            historyRecordsCount: maintenanceHistory?.length || 0
          });

          return {
            id: `maintenance-${job.id}`,
            icon: Wrench,
            title: jobName,
            subtitle: hasDate ? `due ${formatDate(nextDue)}` : 'Not scheduled',
            category: job.category || 'default',
            dueDate: nextDue,
            hasDate,
            isOverdue,
            isDueSoon,
            status,
            module: 'maintenance'
          };
        })
        // FT-92 Fix: Include all jobs with valid dates (removed overly restrictive filter)
        .filter(item => item.hasDate)
        .sort((a, b) => {
          // Items without dates go to end
          if (!a.hasDate && b.hasDate) return 1;
          if (a.hasDate && !b.hasDate) return -1;

          // Normal sorting for items with dates
          if (a.isOverdue && !b.isOverdue) return -1;
          if (!a.isOverdue && b.isOverdue) return 1;
          return a.dueDate - b.dueDate;
        })
        .slice(0, 10); // Show up to 10 maintenance items (scrollable)

      if (maintenanceItems.length > 0) {
        result.push({
          id: 'maintenance',
          title: 'Maintenance',
          icon: Wrench,
          iconColor: 'text-blue-600',
          bgColor: 'bg-blue-50',
          module: 'maintenance',
          items: maintenanceItems
        });
      }
    } catch (error) {
      console.error('[useDashboardCategories] Maintenance error:', error);
    }

    // ========================================
    // CREW CATEGORY - Uses SAME getCrewCompliance() as crew tab
    // ========================================
    try {
      const crewItems = [];

      (crew || []).forEach(crewMember => {
        // Skip archived crew (same filter as crew tab)
        const status = (crewMember.status || 'active').toLowerCase();
        if (status === 'archived') return;

        const crewName = crewMember.name ||
          crewMember.full_name ||
          `${crewMember.firstName || ''} ${crewMember.lastName || ''}`.trim() ||
          'Crew Member';

        // Get profile pic if available
        const profilePic = crewMember.profilePic || crewMember.photo || null;

        // Use the SAME compliance function the crew tab uses
        const compliance = getCrewCompliance(
          crewMember,
          hoursData,
          currentVessel,
          drillsStorageData,
          familiarisationRecords,
          familiarisationValidityMonths
        );

        // Convert compliance issues to dashboard items
        compliance.issues.forEach((issue, idx) => {
          const isError = issue.type === 'error';
          const isWarning = issue.type === 'warning';

          crewItems.push({
            id: `crew-${crewMember.id}-${issue.category}-${idx}`,
            icon: Users,
            title: crewName,
            subtitle: issue.text,
            profilePic,
            dueDate: today,
            isOverdue: isError,
            isDueSoon: isWarning,
            status: isError ? 'overdue' : isWarning ? 'due-soon' : 'ok',
            module: 'crew',
            issueType: issue.category
          });
        });
      });

      // Sort by priority: overdue first, then by date
      const sortedCrewItems = crewItems
        .sort((a, b) => {
          if (a.isOverdue && !b.isOverdue) return -1;
          if (!a.isOverdue && b.isOverdue) return 1;
          return a.dueDate - b.dueDate;
        })
        .slice(0, 10); // Show up to 10 items (scrollable)

      if (sortedCrewItems.length > 0) {
        result.push({
          id: 'crew',
          title: 'Crew',
          icon: Users,
          iconColor: 'text-green-600',
          bgColor: 'bg-green-50',
          module: 'crew',
          items: sortedCrewItems
        });
      }
    } catch (error) {
      console.error('[useDashboardCategories] Crew error:', error);
    }

    // ========================================
    // DEFECTS CATEGORY
    // ========================================
    try {
      const defectItems = (defectsData?.defects || [])
        .filter(defect => {
          const status = (defect.status || '').toLowerCase();
          return status !== 'resolved' && status !== 'closed';
        })
        .map(defect => {
          const raisedDate = new Date(defect.dateRaised || defect.reportedDate || defect.createdAt || Date.now());
          const priority = (defect.priority || '').toLowerCase();
          const severity = (defect.severity || '').toLowerCase();
          const isCritical = priority === 'critical' || priority === 'high' || severity === 'critical' || severity === 'high';
          const isMajor = priority === 'major' || priority === 'medium' || severity === 'major' || severity === 'medium';

          return {
            id: `defect-${defect.id}`,
            icon: Wrench, // Use Wrench icon for defects (issues, not physical items)
            title: defect.title || defect.description || 'Defect',
            subtitle: `raised ${formatDate(raisedDate)}`,
            // No category field - defects use icon only
            dueDate: raisedDate,
            isOverdue: isCritical,
            isDueSoon: isMajor,
            status: isCritical ? 'overdue' : isMajor ? 'due-soon' : 'ok',
            module: 'defects'
          };
        })
        .filter(item => !isNaN(item.dueDate.getTime()))
        .sort((a, b) => {
          if (a.isOverdue && !b.isOverdue) return -1;
          if (!a.isOverdue && b.isOverdue) return 1;
          return b.dueDate - a.dueDate; // Most recent first
        })
        .slice(0, 10); // Show up to 10 defects (scrollable)

      if (defectItems.length > 0) {
        result.push({
          id: 'defects',
          title: 'Defects',
          icon: AlertCircle,
          iconColor: 'text-red-600',
          bgColor: 'bg-red-50',
          module: 'defects',
          items: defectItems
        });
      }
    } catch (error) {
      console.error('[useDashboardCategories] Defects error:', error);
    }

    // ========================================
    // ENSURE ALL 4 CORE CATEGORIES ALWAYS SHOW
    // ========================================
    const coreCategories = [
      {
        id: 'drills',
        title: 'Drills',
        icon: Flame,
        iconColor: 'text-white',
        bgColor: 'bg-slate-800',
        borderColor: 'border-slate-700',
        module: 'drills'
      },
      {
        id: 'maintenance',
        title: 'Maintenance',
        icon: Wrench,
        iconColor: 'text-white',
        bgColor: 'bg-slate-800',
        borderColor: 'border-slate-700',
        module: 'maintenance'
      },
      {
        id: 'crew',
        title: 'Crew',
        icon: Users,
        iconColor: 'text-white',
        bgColor: 'bg-slate-800',
        borderColor: 'border-slate-700',
        module: 'crew'
      },
      {
        id: 'defects',
        title: 'Defects',
        icon: AlertCircle,
        iconColor: 'text-white',
        bgColor: 'bg-slate-800',
        borderColor: 'border-slate-700',
        module: 'defects'
      }
    ];

    // Ensure all core categories exist in result (with items or empty)
    const orderedResult = coreCategories.map(coreCategory => {
      const existing = result.find(cat => cat.id === coreCategory.id);
      if (existing) {
        // Merge existing items with core category styling
        return { ...coreCategory, items: existing.items };
      }
      // Return core category with empty items
      return { ...coreCategory, items: [] };
    });

    return orderedResult;
  // FT-471: added drillList to deps (replaces drillRegister from drill_register_<vesselId>)
  }, [drillsData, drillList, crew, defectsData, maintenanceSchedule, maintenanceHistory, equipmentRegister, advanceWarningDays, currentVesselId, hoursData, familiarisationRecords, familiarisationValidityMonths, drillsStorageData, currentVessel]);

  return categories;
};

// ========================================
// HELPER FUNCTIONS
// ========================================

/**
 * FT-140 FIX: Parse date string as local time, avoiding timezone shift
 * new Date('2026-03-25') parses as UTC midnight, which can shift by ±1 day locally
 * This function handles both YYYY-MM-DD and ISO timestamps correctly
 */
function parseLocalDate(dateStr) {
  if (!dateStr) return null;
  if (typeof dateStr === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    // Date-only string (YYYY-MM-DD) - parse as local time
    const [year, month, day] = dateStr.split('-').map(Number);
    return new Date(year, month - 1, day); // month is 0-indexed
  }
  return new Date(dateStr);
}

function formatDate(date) {
  if (!date) return '';
  const d = parseLocalDate(date);
  if (!d || isNaN(d.getTime())) return '';
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = String(d.getFullYear()).slice(-2);
  return `${day}/${month}/${year}`;
}

/**
 * FT-140 FIX: Calculate next due date using CALENDAR MONTH MATH (maritime standard)
 * Per MCA & ISM Code, maintenance intervals use calendar months, not fixed days:
 * - Monthly: setMonth() → March 25 + 1 month = April 25 (NOT +30 days = April 24)
 * - Annual: setFullYear() → March 25 + 1 year = March 25 next year
 * - Weekly: +7 days is fine
 *
 * This function MUST match MaintenanceJobCardEnhanced.js (source of truth)
 */
function calculateNextDue(fromDate, interval, unit) {
  if (!interval || !unit) return null;

  const date = new Date(fromDate);
  const normalizedUnit = unit.toLowerCase().replace(/s$/, '');

  switch (normalizedUnit) {
    case 'day':
      date.setDate(date.getDate() + interval);
      break;
    case 'week':
      date.setDate(date.getDate() + (interval * 7));
      break;
    case 'month':
      date.setMonth(date.getMonth() + interval);
      break;
    case 'year':
      date.setFullYear(date.getFullYear() + interval);
      break;
    default:
      return null;
  }

  return date;
}
